// src/pages/seeker/SeekerReferences.jsx

import React, { useEffect, useState, useRef } from 'react';
import { seekerAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { X, Check, Loader, Copy, Trash2, MoreVertical, User, Phone, Mail, Pencil, Building2, Briefcase, ChevronUp, ChevronDown } from 'lucide-react';
import { PhoneCapture, parsePhoneStr, joinPhoneStr } from '../../components/shared/PhoneInput';
import { FieldError, useConfirm, AddBtn, usePersistedState } from '../../components/shared/UI';

const C = {
  navy: '#0e4e95', white: '#ffffff',
  offwhite: '#f4f7fb', text: '#1a1a1a', muted: '#666',
  border: '#d0daea',
};

const FL = ({ label, required, children, hint, half }) => (
  <div style={{ marginBottom: '14px', width: half ? 'calc(50% - 6px)' : '100%' }}>
    <label className="form-label">{label}{required && <span style={{ color: '#CC0000', marginLeft: '2px' }}>*</span>}</label>
    {children}
    {hint && <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '3px' }}>{hint}</div>}
  </div>
);

// ── Employer combobox ──────────────────────────────────
const EmployerCombobox = ({ value, onChange, employers }) => {
  const [input,   setInput]   = useState(value || '');
  const [showSug, setShowSug] = useState(false);
  const ref = useRef();

  useEffect(() => { setInput(value || ''); }, [value]);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setShowSug(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = employers.filter(e => e.toLowerCase().includes(input.toLowerCase()));
  const select = (val) => { setInput(val); onChange(val); setShowSug(false); };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <input value={input}
        onChange={e => { setInput(e.target.value); onChange(e.target.value); setShowSug(true); }}
        onFocus={() => setShowSug(true)}
        placeholder="e.g. Deloitte" className="form-input" />
      {showSug && filtered.length > 0 && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 9999, background: C.white, border: `1px solid ${C.border}`, borderRadius: '3px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', marginTop: '2px', maxHeight: '140px', overflowY: 'auto' }}>
          {filtered.map((e, i) => (
            <button key={i} onMouseDown={() => select(e)}
              style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', fontSize: '13px', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text)', borderBottom: `1px solid ${C.border}` }}
              onMouseEnter={ev => ev.currentTarget.style.background = C.offwhite}
              onMouseLeave={ev => ev.currentTarget.style.background = 'none'}>
              {e}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Convert API record → form shape
const refFromApi = (r) => {
  const ph = parsePhoneStr(r.phone);
  return {
    id:          r.id,
    fullName:    r.name     || '',
    position:    r.jobTitle || '',
    company:     r.company  || '',
    phoneCode:   ph.code    || '+61',
    phoneNumber: ph.number,
    phoneLabel:  r.phoneLabel || '',
    email:       r.email    || '',
  };
};

// Convert form shape → API payload
const refToApi = (form) => ({
  name:       form.fullName.trim(),
  jobTitle:   form.position?.trim()   || null,
  company:    form.company?.trim()    || null,
  email:      form.email?.trim()      || null,
  phone:      joinPhoneStr(form.phoneCode, form.phoneNumber),
  phoneLabel: form.phoneLabel?.trim() || null,
});

const emptyRef = () => ({
  id:          crypto.randomUUID(),
  fullName:    '',
  position:    '',
  company:     '',
  phoneCode:   '+61',
  phoneNumber: '',
  phoneLabel:  '',
  email:       '',
  _isNew:      true,
});

// ── Card menu ──────────────────────────────────────────
const CardMenu = ({ onEdit, onDuplicate, onDelete }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const Item = ({ onClick, icon, label, danger }) => (
    <button onClick={() => { onClick(); setOpen(false); }}
      style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '9px 14px', fontSize: '13px', background: 'none', border: 'none', cursor: 'pointer', color: danger ? 'var(--danger)' : 'var(--text)', borderBottom: danger ? 'none' : `1px solid ${C.border}` }}
      onMouseEnter={e => e.currentTarget.style.background = danger ? '#fff5f5' : C.offwhite}
      onMouseLeave={e => e.currentTarget.style.background = 'none'}>
      {icon} {label}
    </button>
  );

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button onClick={() => setOpen(o => !o)}
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.8)', display: 'flex', padding: '4px', borderRadius: '3px', flexShrink: 0 }}
        onMouseEnter={e => e.currentTarget.style.color = '#fff'}
        onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.8)'}>
        <MoreVertical size={16} />
      </button>
      {open && (
        <div style={{ position: 'absolute', right: 0, top: '100%', zIndex: 9999, background: C.white, border: `1px solid ${C.border}`, borderRadius: '4px', boxShadow: '0 4px 16px rgba(0,0,0,0.18)', minWidth: '140px', overflow: 'hidden' }}>
          <Item onClick={onEdit}      icon={<Pencil size={13} />} label="Edit" />
          <Item onClick={onDuplicate} icon={<Copy size={13} />}   label="Duplicate" />
          <Item onClick={onDelete}    icon={<Trash2 size={13} />} label="Delete" danger />
        </div>
      )}
    </div>
  );
};

// ── Display mode ───────────────────────────────────────
const RefDisplay = ({ data: r, onEdit, onDuplicate, onDelete }) => {
  const [open, setOpen] = usePersistedState(`tikhet:seeker:ref:${r.id}:open`, true);
  return (
    <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '6px', marginBottom: '14px', overflow: 'visible' }}>
      <div
        style={{ padding: '8px 12px', background: '#4A6FA5', borderRadius: open ? '6px 6px 0 0' : '6px', display: 'flex', alignItems: 'center', gap: '10px', position: 'relative', cursor: 'pointer', userSelect: 'none' }}
        onClick={(e) => {
          if (e.target.closest('button, [data-cardmenu]')) return;
          setOpen(o => !o);
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0, overflow: 'hidden' }}>
          <span style={{ color: '#E8EDF2', fontSize: '14px', fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textTransform: 'uppercase', letterSpacing: '0.05em', flexShrink: 1 }}>
            {r.fullName || <span style={{ fontStyle: 'italic', opacity: 0.6, fontWeight: 400, textTransform: 'none' }}>Unnamed reference</span>}
          </span>
          {r.email       && <span title={r.email}   style={{ flexShrink: 0 }}><Mail size={14} color="rgba(255,255,255,0.75)" /></span>}
          {r.phoneNumber && <span title={[joinPhoneStr(r.phoneCode, r.phoneNumber), r.phoneLabel].filter(Boolean).join(' — ')} style={{ flexShrink: 0 }}><Phone size={14} color="rgba(255,255,255,0.75)" /></span>}
        </div>
        <CardMenu onEdit={onEdit} onDuplicate={onDuplicate} onDelete={onDelete} />
        {open ? <ChevronUp size={14} color="#E8EDF2" style={{ opacity: 0.85, flexShrink: 0 }} /> : <ChevronDown size={14} color="#E8EDF2" style={{ opacity: 0.85, flexShrink: 0 }} />}
      </div>

      {open && (
        <div style={{ padding: '12px 16px 14px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {r.position && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Briefcase size={15} color={C.muted} style={{ flexShrink: 0 }} />
              <span style={{ fontSize: '14px', color: C.text }}>{r.position}</span>
            </div>
          )}
          {r.company ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Building2 size={15} color={C.muted} style={{ flexShrink: 0 }} />
              <span style={{ fontSize: '14px', color: C.text }}>{r.company}</span>
            </div>
          ) : (
            <span style={{ fontSize: '13px', color: C.muted, fontStyle: 'italic' }}>No company added</span>
          )}
        </div>
      )}
    </div>
  );
};

// ── Edit mode ──────────────────────────────────────────
const RefEdit = ({ data: r, onSave, onCancel, employers }) => {
  const [form,   setForm]   = useState({ ...r });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const set = (field) => (v) => setForm(f => ({ ...f, [field]: v && v.target ? v.target.value : v }));

  const handleSave = async () => {
    const errs = {};
    if (!form.fullName.trim()) errs.fullName = 'Full name is required';
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});
    setSaving(true);
    try { await onSave(form); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ background: C.white, border: `2px solid ${C.blue}`, borderRadius: '6px', marginBottom: '14px', overflow: 'visible' }}>
      <div style={{ background: '#f0f6ff', padding: '10px 14px', borderBottom: `1px solid ${C.border}`, borderRadius: '6px 6px 0 0' }}>
        <span style={{ fontSize: '13px', fontWeight: 700, color: C.blue, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Editing Reference</span>
      </div>

      <div style={{ padding: '16px' }}>
        <FL label="Full Name" required>
          <input value={form.fullName} onChange={v => { set('fullName')(v); setErrors(e => ({ ...e, fullName: '' })); }} placeholder="e.g. Jane Smith" className="form-input" />
          {errors.fullName && <FieldError>{errors.fullName}</FieldError>}
        </FL>
        <FL label="Position Title">
          <input value={form.position || ''} onChange={set('position')} placeholder="e.g. Finance Manager" className="form-input" />
        </FL>
        <FL label="Company / Organisation">
          <EmployerCombobox value={form.company} onChange={val => setForm(f => ({ ...f, company: val }))} employers={employers} />
        </FL>
        <FL label="Email Address">
          <input type="email" value={form.email} onChange={set('email')} placeholder="e.g. jane@company.com" className="form-input" />
        </FL>
        <FL label="Phone Number">
          <PhoneCapture
            code={form.phoneCode} number={form.phoneNumber} label={form.phoneLabel}
            onCodeChange={set('phoneCode')}
            onNumberChange={set('phoneNumber')}
            onLabelChange={set('phoneLabel')}
          />
        </FL>
      </div>

      <div style={{ padding: '10px 16px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'flex-end', gap: '8px', background: C.offwhite, borderRadius: '0 0 4px 4px' }}>
        <button onClick={onCancel} disabled={saving} className="btn btn-danger" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          <X size={13} /> Cancel
        </button>
        <button onClick={handleSave} disabled={saving} className="btn btn-success" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          {saving ? <><Loader size={13} /> Saving…</> : <><Check size={13} /> Save</>}
        </button>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════
// MAIN PAGE
// ══════════════════════════════════════════════════════
export const SeekerReferences = () => {
  const { confirm, dialog } = useConfirm();
  const [references, setReferences] = useState([]);
  const [employers,  setEmployers]  = useState([]);
  const [editing,    setEditing]    = useState(null);
  const [loading,    setLoading]    = useState(true);

  const load = async () => {
    try {
      const [refRes, expRes] = await Promise.all([
        seekerAPI.getReferences(),
        seekerAPI.getExperience(),
      ]);
      setReferences((refRes.data.data || []).map(refFromApi));
      // Pull unique employer names from experience for combobox
      const exps = expRes.data.data || [];
      setEmployers([...new Set(exps.map(e => e.company).filter(Boolean))]);
    } catch { toast.error('Failed to load references'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleSaveCard = async (form) => {
    const payload = refToApi(form);
    try {
      if (form._isNew) {
        await seekerAPI.addReference(payload);
      } else {
        await seekerAPI.updateReference(form.id, payload);
      }
      await load();
      setEditing(null);
      toast.success('Reference saved');
    } catch { toast.error('Failed to save reference'); }
  };

  const handleCancelCard = (id) => {
    const r = references.find(r => r.id === id);
    if (r?._isNew) setReferences(prev => prev.filter(r => r.id !== id));
    setEditing(null);
  };

  const handleDuplicate = async (id) => {
    const r = references.find(r => r.id === id);
    if (!r) return;
    try {
      await seekerAPI.addReference(refToApi(r));
      await load();
      toast.success('Reference duplicated');
    } catch { toast.error('Failed to duplicate'); }
  };

  const handleDelete = async (id) => {
    if (!await confirm('Delete Reference', 'Are you sure you want to delete this reference?')) return;
    try {
      await seekerAPI.deleteReference(id);
      setReferences(prev => prev.filter(r => r.id !== id));
      if (editing === id) setEditing(null);
      toast.success('Reference deleted');
    } catch { toast.error('Failed to delete reference'); }
  };

  const handleAddNew = () => {
    const r = emptyRef();
    setReferences(prev => [...prev, r]);
    setEditing(r.id);
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>;

  return (
    <div className="fade-in">
      {dialog}
      <div className="page-header">
        <h1>References</h1>
        <p>Your professional references — used across all your CVs</p>
      </div>

      <div className="page-content">
        <div style={{ marginBottom: '16px' }}>
          {references.length === 0 && (
            <div style={{ textAlign: 'center', padding: '32px 0', color: C.muted, background: C.white, border: `1px solid ${C.border}`, borderRadius: '6px', marginBottom: '14px' }}>
              <User size={36} color={C.border} style={{ marginBottom: '10px' }} />
              <p style={{ fontSize: '13px', margin: 0 }}>No references added yet</p>
            </div>
          )}

          {references.map(r =>
            editing === r.id ? (
              <RefEdit key={r.id} data={r} onSave={handleSaveCard} onCancel={() => handleCancelCard(r.id)} employers={employers} />
            ) : (
              <RefDisplay key={r.id} data={r} onEdit={() => setEditing(r.id)} onDuplicate={() => handleDuplicate(r.id)} onDelete={() => handleDelete(r.id)} />
            )
          )}

          <AddBtn onClick={handleAddNew} size={15} title="Add Reference" />
        </div>
      </div>
    </div>
  );
};
